import React, { useCallback, useEffect, useState } from "react";

import { Loader2, Save } from "lucide-react";

import CommonModal from "../../Components/Modal";
import { useAppDispatch } from "../../Store/Hooks";
import {
  fetchVendorConfigRaw,
  upsertVendorConfigRaw,
} from "../../Store/Reducers/VendorAdminSlice";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import { toast } from "../../Utils/Helper/toast";
import {
  DEFAULT_LOW_SCORE_CRITERIA,
  LOW_SCORE_CRITERIA_KEY,
  normalizeLowScoreCriteria,
} from "../../Utils/LowScoreCriteria";
import LowScoreCriteriaForm from "../Settings/LowScoreCriteriaForm";
import NeedsAttentionCriteriaForm from "../Settings/NeedsAttentionCriteriaForm";
import {
  DEFAULT_PORTFOLIO_NEEDS_ATTENTION,
  normalizePortfolioNeedsAttention,
  type PortfolioNeedsAttentionSettings,
} from "../Settings/PortfolioNeedsAttentionOrgSettings";
import PriorityScoringWeightsForm, {
  DEFAULT_PRIORITY_SCORING_WEIGHTS,
  normalizePriorityScoringWeights,
  type PriorityScoringWeights,
} from "../Settings/PriorityScoringWeightsForm";
import SignalEngineThresholdsForm, {
  DEFAULT_SIGNAL_ENGINE_THRESHOLDS,
  normalizeSignalEngineThresholds,
  type SignalEngineThresholds,
} from "../Settings/SignalEngineThresholdsForm";

const NEEDS_ATTENTION_KEY = "portfolio_needs_attention";
const SIGNAL_ENGINE_THRESHOLDS_KEY = "signal_engine_thresholds";
const PRIORITY_SCORING_WEIGHTS_KEY = "priority_scoring_weights";

interface SectionProps<T> {
  title: string;
  description: string;
  fetchUrl: () => string;
  vendorConfigKey: string;
  defaultValue: T;
  normalize: (raw: unknown) => T;
  render: (value: T, onChange: (patch: Partial<T>) => void) => React.ReactNode;
}

function extractValue(payload: unknown): unknown {
  if (!payload || typeof payload !== "object") return undefined;
  const record = payload as Record<string, unknown>;
  if (record.value !== undefined) return record.value;
  if (record.content && typeof record.content === "object") {
    const content = record.content as Record<string, unknown>;
    if (content.value !== undefined) return content.value;
  }
  return record;
}

/** One self-contained load/save section — each of the modal's 4 settings
 * areas has its own vendor_config key and its own Save action, matching the
 * established per-setting pattern already used on the standalone settings
 * pages (LowScoreCriteriaOrgSettings, PortfolioNeedsAttentionOrgSettings)
 * rather than one shared submit for the whole modal. */
function SettingsSection<T>({
  title,
  description,
  fetchUrl,
  vendorConfigKey,
  defaultValue,
  normalize,
  render,
}: SectionProps<T>) {
  const dispatch = useAppDispatch();
  const [value, setValue] = useState<T>(defaultValue);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dispatch(
        fetchVendorConfigRaw(fetchUrl())
      ).unwrap();
      const status = (response as { status?: string })?.status;
      if (status === "failure" || status === "unauthorized") {
        setValue(defaultValue);
        return;
      }
      setValue(normalize(extractValue(response)));
    } catch {
      setValue(defaultValue);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, fetchUrl, vendorConfigKey]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    const normalized = normalize(value);
    setSaving(true);
    try {
      const response = await dispatch(
        upsertVendorConfigRaw({ key: vendorConfigKey, value: normalized })
      ).unwrap();
      const status = (response as { status?: string })?.status;
      if (status === "failure" || status === "unauthorized") {
        toast.error(
          (response as { message?: string })?.message ||
            `Failed to save ${title}`
        );
        return;
      }
      setValue(normalize(extractValue(response) ?? normalized));
      toast.success(`${title} saved`);
    } catch (error: unknown) {
      // The thunk rejects with the error's message (often the JSON body).
      const errorMessage = typeof error === "string" ? error : "";
      let message = `Failed to save ${title}`;
      try {
        const parsed = JSON.parse(errorMessage || "{}");
        if (parsed?.message) message = parsed.message;
      } catch {
        if (errorMessage) message = errorMessage;
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <h3 className="mb-1 text-sm font-bold text-gray-900">{title}</h3>
      <p className="mb-4 text-xs text-gray-500">{description}</p>
      {loading ? (
        <div className="flex h-8 items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
          Loading…
        </div>
      ) : (
        <>
          {render(value, patch => setValue(prev => ({ ...prev, ...patch })))}
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-purple-700 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-purple-800 disabled:opacity-60"
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Save
            </button>
          </div>
        </>
      )}
    </div>
  );
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

/** Unified Portfolio Settings modal (mock's "⚙ Portfolio Settings" header
 * button, ~3616-3877) — consolidates the 4 org-level settings that used to
 * only exist as separate read-only tile popovers or standalone /settings
 * pages: Needs Attention Criteria, Low Score Criteria, Signal Engine
 * Thresholds, and Priority Scoring Weights. */
const PortfolioSettingsModal: React.FC<Props> = ({ isOpen, onClose }) => {
  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      title="Portfolio Settings"
      size="2xl"
      containerClassName="!max-h-[85vh]"
    >
      <div className="flex flex-col gap-4 overflow-y-auto p-1">
        <SettingsSection<PortfolioNeedsAttentionSettings>
          title="Needs Attention Criteria"
          description="Accounts matching any active criterion are flagged as Needs Attention on My Accounts."
          fetchUrl={API_URLS.fetchPortfolioNeedsAttentionConfig}
          vendorConfigKey={NEEDS_ATTENTION_KEY}
          defaultValue={DEFAULT_PORTFOLIO_NEEDS_ATTENTION}
          normalize={raw =>
            normalizePortfolioNeedsAttention(
              raw as Partial<PortfolioNeedsAttentionSettings>
            )
          }
          render={(value, onChange) => (
            <NeedsAttentionCriteriaForm value={value} onChange={onChange} />
          )}
        />

        <SettingsSection
          title="Low Score Criteria"
          description="Relationship pairs matching any active criterion are flagged as Low Score on Relationship Priorities."
          fetchUrl={API_URLS.fetchLowScoreCriteriaConfig}
          vendorConfigKey={LOW_SCORE_CRITERIA_KEY}
          defaultValue={DEFAULT_LOW_SCORE_CRITERIA}
          normalize={raw =>
            normalizeLowScoreCriteria(
              raw as Parameters<typeof normalizeLowScoreCriteria>[0]
            )
          }
          render={(value, onChange) => (
            <LowScoreCriteriaForm value={value} onChange={onChange} />
          )}
        />

        <SettingsSection<SignalEngineThresholds>
          title="Signal Engine Thresholds"
          description="Numeric boundaries the public-data signal engine uses to derive Business Health class, risk tier, Fit tier, and expansion/portfolio-risk signals."
          fetchUrl={API_URLS.fetchSignalEngineThresholdsConfig}
          vendorConfigKey={SIGNAL_ENGINE_THRESHOLDS_KEY}
          defaultValue={DEFAULT_SIGNAL_ENGINE_THRESHOLDS}
          normalize={normalizeSignalEngineThresholds}
          render={(value, onChange) => (
            <SignalEngineThresholdsForm value={value} onChange={onChange} />
          )}
        />

        <SettingsSection<PriorityScoringWeights>
          title="Priority Scoring Weights"
          description="Relative weight each factor carries in the Relationship Priorities urgency score. 1.00× reproduces the default scoring."
          fetchUrl={API_URLS.fetchPriorityScoringWeightsConfig}
          vendorConfigKey={PRIORITY_SCORING_WEIGHTS_KEY}
          defaultValue={DEFAULT_PRIORITY_SCORING_WEIGHTS}
          normalize={normalizePriorityScoringWeights}
          render={(value, onChange) => (
            <PriorityScoringWeightsForm value={value} onChange={onChange} />
          )}
        />
      </div>
    </CommonModal>
  );
};

export default PortfolioSettingsModal;
